import { useEffect, useRef, useState } from "react";
import { AppIcon } from "../AppIcon";
import type { ApiClient } from "../../api/client";
import type { QrPayment, Ride } from "../../api/types";
import { addressOf, paymentOf } from "../../features/orders/order-view";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { QrCode } from "../../ui/QrCode";
import { BankQrPanel } from "../BankQrPanel";
import { epcQr } from "../waiter/waiter";

interface Props {
  api: ApiClient;
  ride: Ride;
  /**
   * Bank-QR at the door when the shop and the owner allow it for this courier; null otherwise.
   * `automatic`: Bancontact makes the QR and confirms the payment (ADR 0024).
   */
  bankQr: { iban: string; name: string; automatic: boolean } | null;
  onBack: () => void;
  /** In the window over the list: its title says the address and its × goes back (Furkan, 18/10/2026). */
  inDialog?: boolean;
  /** "contant € 24,50", "bank-QR € 24,50" or null when nothing was due. */
  onDelivered: (how: string | null) => void;
  report: (error: unknown) => void;
}

/**
 * "Geleverd" at the door (prototype paneHtml deliv): paid beforehand: nothing to collect; otherwise the
 * amount, "Contant ontvangen · afgeleverd" or the bank-QR the customer scans. In a shop with a key of
 * Bancontact Pro the QR is Bancontact's and the ride is marked delivered the moment Bancontact says it is
 * paid (ADR 0024); otherwise the courier confirms when the customer's bank app shows it paid.
 */
export function DeliverPane({ api, ride, bankQr, onBack, inDialog = false, onDelivered, report }: Props) {
  const text = t.tafoApp.courier;
  const [qr, setQr] = useState(false);
  const [busy, setBusy] = useState(false);
  const due = ride.totalCents - ride.paidCents;

  /** Bancontact has the money and marking the ride delivered failed: said under the QR. */
  const [notBooked, setNotBooked] = useState(false);

  /** `confirmed`: the bank-QR that Bancontact says is paid. */
  const deliver = async (method: "cash" | "bank_qr" | null, confirmed?: QrPayment) => {
    setBusy(true);
    try {
      await api.request("POST", `/device/deliveries/${ride.id}/delivered`, {
        body: method === null ? {} : { method, ...(confirmed !== undefined && { qrPaymentId: confirmed.id }) },
      });
      onDelivered(
        method === null || due <= 0
          ? null
          : method === "cash"
            ? text.cashHow(formatEuro(due))
            : text.qrHow(formatEuro(due)),
      );
    } catch (error) {
      if (confirmed !== undefined) setNotBooked(true);
      report(error);
      setBusy(false);
    }
  };

  // A bank-QR of this ride that was paid while nobody looked (the phone went dark at the door): the
  // ride is delivered and paid as soon as the courier opens it again.
  const automatic = bankQr?.automatic === true;
  const looked = useRef(false);
  const again = useRef(deliver);
  useEffect(() => {
    again.current = deliver;
  });
  useEffect(() => {
    if (!automatic || due <= 0 || looked.current) return;
    looked.current = true;
    api
      .request<{ qrPayments: QrPayment[] }>("GET", `/device/qr-payments?orderId=${ride.id}`)
      .then((open) => {
        const found = open.qrPayments.find((qr) => qr.status === "succeeded" && !qr.booked && qr.amountCents === due);
        if (found) void again.current("bank_qr", found);
      })
      .catch(report);
  }, [api, automatic, due, ride.id, report]);

  const head = inDialog ? null : (
    <>
      <button type="button" className="linkbtn" onClick={onBack}>
        {t.tafoApp.waiter.back}
      </button>
      <h3>{addressOf(ride)}</h3>
    </>
  );

  if (due <= 0) {
    return (
      <>
        {head}
        <p>
          <span className="pill ok">{t.couriers.paid(paymentOf(ride))}</span>
        </p>
        <p>{text.nothingToCollect}</p>
        <button type="button" className="btn primary ph-big" disabled={busy} onClick={() => void deliver(null)}>
          <AppIcon name="check" />
          {text.handedOver}
        </button>
      </>
    );
  }

  if (qr && bankQr?.automatic === true) {
    return (
      <>
        {head}
        <BankQrPanel
          api={api}
          amountCents={due}
          tipCents={0}
          orderId={ride.id}
          scanText={text.qrScan}
          onPaid={(paid) => void deliver("bank_qr", paid)}
          booking={busy}
          notBooked={notBooked}
          onLeave={() => {
            setNotBooked(false);
            setQr(false);
          }}
          leaveText={text.cashAfterAll}
        />
      </>
    );
  }

  if (qr && bankQr !== null) {
    return (
      <>
        {head}
        <p className="ob-qrhead">
          {text.qrScan}
          <br />
          <b className="num">{formatEuro(due)}</b>
        </p>
        <div className="ph-qr sm">
          <QrCode text={epcQr(bankQr.name, bankQr.iban, due, `Tafo ${ride.code}`)} label={text.qrScan} />
        </div>
        <p className="proto c">{text.qrCheck}</p>
        <button type="button" className="btn primary ph-big" disabled={busy} onClick={() => void deliver("bank_qr")}>
          <AppIcon name="check" />
          {text.qrDone}
        </button>
        <button
          type="button"
          className="linkbtn"
          onClick={() => {
            setQr(false);
          }}
        >
          {text.cashAfterAll}
        </button>
      </>
    );
  }

  return (
    <>
      {head}
      <p className="c ob-due">
        {text.toCollect}
        <br />
        <b className="num">{formatEuro(due)}</b>
      </p>
      <button type="button" className="btn primary ph-big" disabled={busy} onClick={() => void deliver("cash")}>
        <AppIcon name="cash" />
        {text.cashReceived}
      </button>
      {bankQr !== null && (
        <button
          type="button"
          className="btn ph-big"
          disabled={busy}
          onClick={() => {
            setQr(true);
          }}
        >
          <AppIcon name="qr" />
          {text.customerQr}
        </button>
      )}
      <p className="proto c">{text.onlineNote}</p>
    </>
  );
}
